// client/src/pages/Admin/ProjectManager/hooks/useProjectFilters.ts
/**
 * USE PROJECT FILTERS HOOK
 * 
 * This hook manages filtering, searching, and sorting of projects.
 * It provides a clean interface for components to filter projects.
 * 
 * @module ProjectManager/Hooks/useProjectFilters
 */

import { useState, useMemo, useCallback } from 'react';
import { Project, ProjectStatus, ProjectCategory, ProjectImpact } from '../types/project.types';
import {
    filterProjects,
    sortProjects,
    projectMatchesSearch
} from '../utils/projectHelpers';
import { SORT_OPTIONS } from '../constants/project.constants';

// ============================================
// TYPE DEFINITIONS
// ============================================

interface FilterState {
    status: ProjectStatus | 'all';
    category: ProjectCategory | 'all';
    impact: ProjectImpact | 'all';
    featured: boolean | 'all';
    isOngoing: boolean | 'all';
}

interface UseProjectFiltersReturn {
    filters: FilterState;
    searchTerm: string;
    sortOption: string;
    sortDirection: 'asc' | 'desc';
    filteredProjects: Project[];
    hasActiveFilters: boolean;
    activeFilterCount: number;
    setSearchTerm: (term: string) => void;
    updateFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
    resetFilters: () => void;
    setSortOption: (option: string) => void;
    toggleSortDirection: () => void;
    getFilterOptions: () => {
        categories: ProjectCategory[];
        statuses: ProjectStatus[];
        impacts: ProjectImpact[];
    };
}

// ============================================
// HOOK IMPLEMENTATION
// ============================================

/**
 * useProjectFilters hook for filtering, searching, and sorting projects
 * 
 * @param projects - Array of projects to filter
 * @param initialFilters - Optional initial filter state
 * 
 * @example
 * const { filteredProjects, filters, updateFilter, resetFilters } = useProjectFilters(projects);
 */
export const useProjectFilters = (
    projects: Project[],
    initialFilters?: Partial<FilterState>
): UseProjectFiltersReturn => {
    // Filter state
    const [filters, setFilters] = useState<FilterState>({
        status: 'all',
        category: 'all',
        impact: 'all',
        featured: 'all',
        isOngoing: 'all',
        ...initialFilters
    });

    // Search state
    const [searchTerm, setSearchTerm] = useState<string>('');
    
    // Sort state
    const [sortOption, setSortOption] = useState<string>('newest');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

    /**
     * Update a single filter
     */
    const updateFilter = useCallback(<K extends keyof FilterState>(
        key: K,
        value: FilterState[K]
    ): void => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    /**
     * Reset all filters to default
     */
    const resetFilters = useCallback((): void => {
        setFilters({
            status: 'all',
            category: 'all',
            impact: 'all',
            featured: 'all',
            isOngoing: 'all'
        });
        setSearchTerm('');
        setSortOption('newest');
        setSortDirection('desc');
    }, []);

    /**
     * Toggle sort direction
     */
    const toggleSortDirection = useCallback((): void => {
        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    }, []);

    /**
     * Apply filters, search, and sorting to projects
     */
    const filteredProjects = useMemo(() => {
        if (!Array.isArray(projects)) return [];

        // Apply filters
        let result = filterProjects(projects, {
            ...filters,
            searchTerm: searchTerm || undefined
        });

        // Apply search if it doesn't match the filter search
        if (searchTerm && !filters.status && !filters.category) {
            result = result.filter(project => 
                projectMatchesSearch(project, searchTerm)
            );
        }

        // Apply sorting
        result = sortProjects(result, sortOption, sortDirection);

        return result;
    }, [projects, filters, searchTerm, sortOption, sortDirection]);

    /**
     * Check if any filters are active
     */
    const hasActiveFilters = useMemo(() => {
        return (
            filters.status !== 'all' ||
            filters.category !== 'all' ||
            filters.impact !== 'all' ||
            filters.featured !== 'all' ||
            filters.isOngoing !== 'all' ||
            searchTerm.length > 0
        );
    }, [filters, searchTerm]);

    /**
     * Count active filters
     */
    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (filters.status !== 'all') count++;
        if (filters.category !== 'all') count++;
        if (filters.impact !== 'all') count++;
        if (filters.featured !== 'all') count++;
        if (filters.isOngoing !== 'all') count++;
        if (searchTerm.length > 0) count++;
        return count;
    }, [filters, searchTerm]);

    /**
     * Get available filter options from projects
     */
    const getFilterOptions = useCallback(() => {
        if (!Array.isArray(projects)) {
            return {
                categories: [],
                statuses: [],
                impacts: []
            };
        }

        const categories = new Set<ProjectCategory>();
        const statuses = new Set<ProjectStatus>();
        const impacts = new Set<ProjectImpact>();

        projects.forEach(project => {
            if (project.category) categories.add(project.category);
            if (project.status) statuses.add(project.status);
            if (project.impact) impacts.add(project.impact);
        });

        return {
            categories: Array.from(categories),
            statuses: Array.from(statuses),
            impacts: Array.from(impacts)
        };
    }, [projects]);

    return {
        filters,
        searchTerm,
        sortOption,
        sortDirection,
        filteredProjects,
        hasActiveFilters,
        activeFilterCount,
        setSearchTerm,
        updateFilter,
        resetFilters,
        setSortOption,
        toggleSortDirection,
        getFilterOptions
    };
};